import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';
import {
  Typography, Card, Tag, Button, Space, Spin, message, Form, Input, Select,
  DatePicker, Divider, Table, Modal, Popconfirm, Row, Col, Descriptions, Badge, Alert, List, Avatar, Tooltip,
} from 'antd';
import {
  EditOutlined, CopyOutlined, PlusOutlined, DeleteOutlined, LinkOutlined, UserOutlined, CheckOutlined, CommentOutlined, BellFilled,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import { ProjectFyi, CreateTaskFromRecord } from '../components/ProjectParts';
import { CommonLinksPicker } from '../components/CoachingCommonLinks';
import ParticipantBulkAdd from '../components/ParticipantBulkAdd';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import ErrorQuestionHistory from '../components/ErrorQuestionHistory';
import LinkifiedText from '../components/LinkifiedText';
import CoachingCreateModal from '../components/CoachingCreateModal';
import { PaperClipOutlined } from '@ant-design/icons';
import { useBackFallback, usePageLabel } from '../components/BackChip';
import UserAvatar from '../components/UserAvatar';

const STATUS_COLOR  = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const STATUS_LABEL  = { draft: 'Draft',   active: 'Active', completed: 'Completed', canceled: 'Canceled' };
const PART_STATUS_COLOR = { pending: 'orange', completed: 'green', canceled: 'red' };
const PART_STATUS_LABEL = { pending: 'Pending', completed: 'Completed', canceled: 'Canceled' };

function canManage(user) {
  return (user?.CanEditCoaching ?? 0) >= 2;
}

export default function CoachingSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  // Projects Access Edit (2); never agents, who cannot use Projects.
  const canCreateTask = currentUser?.Role !== 'agent' && (currentUser?.CanEditProjects ?? 0) >= 2;
  const [taskOpen, setTaskOpen] = useState(false);
  const [fyiKey, setFyiKey] = useState(0);   // remounts the Project FYI after Create Task
  // The Back chip (Layout): back where you came from, else this list.
  const staffCoach = (currentUser?.CanEditCoaching ?? 0) >= 1;
  useBackFallback(staffCoach ? '/coaching/sessions' : '/coaching/my-sessions', staffCoach ? 'Coaching Sessions' : 'My Coaching Sessions');
  usePageLabel(`Coaching Session #${sessionId}`);

  const [session, setSession]   = useState(null);
  const [loading, setLoading]   = useState(true);

  // Edit session modal
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving]     = useState(false);
  // Activating with a due date of today or earlier: offer a new date first.
  // { values } = the edit form's values waiting to be saved.
  const [dueCheck, setDueCheck] = useState(null);
  const [newDue,   setNewDue]   = useState(null);
  const [editForm] = Form.useForm();

  // Options for edit modal
  const [areas, setAreas]           = useState([]);
  const [coaches, setCoaches]       = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  // Watched so the bulk-add control can append to the current selection.
  const watchedParticipants = Form.useWatch('participant_ids', editForm);
  const watchedViewers      = Form.useWatch('viewer_ids', editForm);

  // Who may be picked. Scoped to the caller's visible teams, but anyone already
  // on the session stays selectable so an edit cannot silently drop them.
  // Computed once and shared by the Select and the bulk-add control, so the two
  // can never offer different people.
  const candidatesFor = (alreadyOn) => {
    const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
    const existing = new Set(alreadyOn || []);
    return activeUsers.filter(
      (u) => existing.has(u.ID) || vtIds.length === 0 || vtIds.includes(u.TeamID)
    );
  };
  const participantCandidates = candidatesFor(session?.Participants?.map((p) => p.UserID));
  const viewerCandidates      = candidatesFor(session?.Viewers?.map((v) => v.UserID));
  const [campaigns, setCampaigns]   = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);

  // Interaction/URL add modals
  const [addInterOpen, setAddInterOpen] = useState(false);
  const [addUrlOpen,   setAddUrlOpen]   = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [interForm] = Form.useForm();
  const [urlForm]   = Form.useForm();
  const [interactionTypes, setInteractionTypes] = useState([]);

  // Edit interaction comment
  const [editInterOpen, setEditInterOpen]   = useState(false);
  const [editingInter,  setEditingInter]    = useState(null);
  const [editInterForm] = Form.useForm();

  // My participant record
  const [myParticipant, setMyParticipant] = useState(null);
  const [partEditOpen, setPartEditOpen]   = useState(false);
  const [partForm] = Form.useForm();

  // Manager editing a participant on their behalf
  const [editingPart,     setEditingPart]     = useState(null);
  const [partMgrEditOpen, setPartMgrEditOpen] = useState(false);
  const [partMgrForm] = Form.useForm();

  // Linked evaluations (cases sharing a ContactID with any interaction)
  const [linkedEvals, setLinkedEvals]           = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [linkedEvalsLoading, setLinkedEvalsLoading] = useState(false);

  // Linked errors (error submissions linked directly via ErrorSubmissionID)
  const [linkedErrors, setLinkedErrors]           = useState([]);
  const [linkedErrorsLoading, setLinkedErrorsLoading] = useState(false);
  // Error whose history modal is open, or null
  const [historyError, setHistoryError] = useState(null);

  // Comments
  const [comments,      setComments]      = useState([]);
  const [addingComment, setAddingComment] = useState(false);
  const [editComment,   setEditComment]   = useState({ id: null, text: '' });

  const loadComments = useCallback(async () => {
    try {
      const res = await api.get(`/api/coaching/sessions/${sessionId}/comments`);
      setComments(res.data || []);
    } catch { /* non-fatal */ }
  }, [sessionId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/coaching/sessions/${sessionId}`);
      setSession(res.data);
      const me = res.data.Participants?.find((p) => p.UserID === currentUser?.ID);
      setMyParticipant(me || null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        message.error('You do not have access to this coaching session.');
        navigate(-1);
      } else if (status === 404) {
        message.error('Coaching session not found.');
        navigate(-1);
      } else {
        message.error('Failed to load session.');
      }
    } finally { setLoading(false); }
  }, [sessionId, currentUser]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadComments(); }, [loadComments]);

  // Fetch evaluations linked to any ContactID in the session's interactions
  useEffect(() => {
    if (!session?.Interactions?.length) { setLinkedEvals([]); return; }
    const contactIds = [...new Set(session.Interactions.map((i) => i.ContactID).filter(Boolean))];
    if (!contactIds.length) { setLinkedEvals([]); return; }
    setLinkedEvalsLoading(true);
    Promise.all(contactIds.map((cid) => api.get('/api/cases', { params: { contact_id: cid } })))
      .then((results) => {
        const all = results.flatMap((r) => r.data);
        const seen = new Set();
        setLinkedEvals(all.filter((c) => { if (seen.has(c.ID)) return false; seen.add(c.ID); return true; }));
      })
      .catch(() => {})
      .finally(() => setLinkedEvalsLoading(false));
  }, [session?.Interactions]);

  // Fetch error submissions linked via ErrorSubmissionID in the session's interactions
  useEffect(() => {
    if (!session?.Interactions?.length) { setLinkedErrors([]); return; }
    const errorIds = [...new Set(session.Interactions.map((i) => i.ErrorSubmissionID).filter(Boolean))];
    if (!errorIds.length) { setLinkedErrors([]); return; }
    setLinkedErrorsLoading(true);
    Promise.all(errorIds.map((eid) => api.get(`/api/errors/${eid}`)))
      .then((results) => {
        setLinkedErrors(results.map((r) => {
          const sub = r.data.submission;
          if (!sub) return null;
          // The response carries the whole form with per-question answers. A
          // question counts as an error when it is not feedback-only and has a
          // non-zero count -- the same rule that renders the red Error tag on
          // the submission page.
          // Category is the form section name -- the same efs.Name the error
          // analytics group by. Only sections that actually contributed an
          // error are listed, and a name is taken once even if several of its
          // questions failed.
          const errored = [];
          const categories = new Set();
          (r.data.form?.Sections || []).forEach((sec) => {
            (sec.Questions || []).forEach((q) => {
              if (!q.FeedbackOnly && (q.ErrorCount ?? 0) > 0) {
                errored.push(q.QuestionText || q.Text || q.Name);
                if (sec.Name) categories.add(sec.Name);
              }
            });
          });
          return {
            ...sub,
            ErrorQuestions:  errored.filter(Boolean),
            ErrorCategories: [...categories],
          };
        }).filter(Boolean));
      })
      .catch(() => {})
      .finally(() => setLinkedErrorsLoading(false));
  }, [session?.Interactions]);

  const openEdit = async () => {
    try {
      const coachParams = { include_user_id: session.CoachUserID };
      if (session.CampaignID) coachParams.campaign_id = session.CampaignID;
      const [areasRes, coachesRes, usersRes, campsRes, rtRes] = await Promise.all([
        api.get('/api/coaching/areas'),
        api.get('/api/coaching/coaches', { params: coachParams }),
        api.get('/api/coaching/active-users'),
        api.get('/api/coaching/campaigns'),
        api.get('/api/coaching/request-types'),
      ]);
      setAreas(areasRes.data);
      setCoaches(coachesRes.data);

      // Inject inactive participants and viewers so they survive an edit-save round-trip
      const usersList = [...usersRes.data];
      const activeUserIds = new Set(usersList.map((u) => u.ID));
      [
        ...(session.Participants || []).map((p) => ({ ID: p.UserID, DisplayName: p.DisplayName })),
        ...(session.Viewers     || []).map((v) => ({ ID: v.UserID, DisplayName: v.DisplayName })),
      ].forEach(({ ID, DisplayName }) => {
        if (!activeUserIds.has(ID)) {
          usersList.push({ ID, DisplayName: `${DisplayName || 'Unknown'} (inactive)`, TeamID: null });
          activeUserIds.add(ID);
        }
      });
      setActiveUsers(usersList);

      setCampaigns(campsRes.data);

      // Inject inactive request type so it stays visible if it has since been deactivated
      const rtList = rtRes.data;
      if (session.RequestTypeID && !rtList.some((t) => t.ID === session.RequestTypeID)) {
        rtList.unshift({ ID: session.RequestTypeID, Name: `${session.RequestTypeName || 'Unknown'} (inactive)` });
      }
      setRequestTypes(rtList);
      editForm.setFieldsValue({
        title:           session.Title,
        status:          session.Status,
        area_id:         session.AreaID,
        coach_user_id:   session.CoachUserID,
        due_date:        session.DueDate ? dayjs(session.DueDate) : null,
        note:            session.Note || '',
        campaign:        session.CampaignID || null,
        request_type_id: session.RequestTypeID || null,
        participant_ids: session.Participants?.map((p) => p.UserID) || [],
        viewer_ids:      session.Viewers?.map((v) => v.UserID) || [],
      });
      setEditOpen(true);
    } catch { message.error('Failed to load options.'); }
  };

  const doSave = async (values) => {
    setSaving(true);
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, {
        title:           values.title,
        status:          values.status,
        // Explicit null, not undefined: JSON.stringify drops undefined keys and
        // the API falls back to the stored AreaID when the key is absent, so
        // clearing the field would silently not stick.
        area_id:         values.area_id ?? null,
        coach_user_id:   values.coach_user_id || null,
        due_date:        values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
        note:            values.note || null,
        campaign_id:     values.campaign || null,
        request_type_id: values.request_type_id || null,
        participant_ids: values.participant_ids || [],
        viewer_ids:      values.viewer_ids || [],
      });
      message.success('Session updated.');
      setEditOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleSave = async () => {
    let values;
    try { values = await editForm.validateFields(); }
    catch { return; }

    // Completing the session does not complete the participants, so a session
    // can close with people still owing their part. Worth a deliberate
    // confirmation rather than silently leaving them pending.
    if (values.status === 'completed' && session.Status !== 'completed') {
      const pending = (session.Participants || []).filter((p) => p.Status === 'pending');
      if (pending.length) {
        const names = pending.map((p) => p.DisplayName).filter(Boolean);
        Modal.confirm({
          title: `${pending.length} participant${pending.length === 1 ? ' is' : 's are'} still pending`,
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>
                {names.length === 1
                  ? `${names[0]} has not completed their part.`
                  : 'These participants have not completed their part:'}
              </div>
              {names.length > 1 && (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {names.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
              <div style={{ marginTop: 8 }}>
                If these participants require no further action/documentation on
                this coaching session, then proceed. Else either you or they can
                update their participant status.
              </div>
            </div>
          ),
          okText: 'Complete Anyway',
          cancelText: 'Go Back',
          onOk: () => doSave(values),
        });
        return;
      }
    }

    // Going Active with a due date that is today or already past: the
    // participants would start out late. Offer a new date (or keep it).
    if (values.status === 'active' && session.Status !== 'active'
        && values.due_date && !values.due_date.isAfter(dayjs(), 'day')) {
      setNewDue(null);
      setDueCheck({ values });
      return;
    }

    if (values.status === 'canceled' && session.Status !== 'canceled') {
      Modal.confirm({
        title: 'Cancel this session?',
        content: 'All participant statuses will be set to Canceled. This cannot be undone.',
        okText: 'Yes, Cancel Session',
        okButtonProps: { danger: true },
        cancelText: 'Go Back',
        onOk: () => doSave(values),
      });
      return;
    }
    doSave(values);
  };

  // Add interaction
  const handleAddInteraction = async () => {
    let values;
    try { values = await interForm.validateFields(); }
    catch { return; }

    try {
      const vr = await api.get('/api/coaching/validate-contact', {
        params: { contact_id: values.contact_id, session_id: sessionId },
      });
      if (!vr.data.valid) {
        message.error(vr.data.error || 'Contact ID is not valid for this session.');
        return;
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not validate Contact ID.');
      return;
    }

    const newList = [
      // error_submission_id and case_id are carried through because the update
      // replaces every interaction from this payload -- omitting them silently
      // severed the link to the error or evaluation the session came from.
      ...(session.Interactions || []).map((i) => ({
        contact_id:          i.ContactID,
        note:                i.Note,
        type_id:             i.TypeID || null,
        error_submission_id: i.ErrorSubmissionID || null,
        case_id:             i.CaseID || null,
      })),
      { contact_id: values.contact_id, note: values.note || null, type_id: values.type_id || null },
    ];
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, { interactions: newList });
      message.success('Interaction added.');
      setAddInterOpen(false);
      load();
    } catch { message.error('Failed to add interaction.'); }
  };

  const handleRemoveInteraction = async (inter) => {
    const newList = (session.Interactions || [])
      .filter((i) => i.ID !== inter.ID)
      .map((i) => ({
        contact_id:          i.ContactID,
        note:                i.Note,
        type_id:             i.TypeID || null,
        error_submission_id: i.ErrorSubmissionID || null,
        case_id:             i.CaseID || null,
      }));
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, { interactions: newList });
      load();
    } catch { message.error('Failed to remove interaction.'); }
  };

  const openEditInter = async (inter) => {
    setEditingInter(inter);
    editInterForm.setFieldsValue({ note: inter.Note || '', type_id: inter.TypeID || null });
    try {
      const res = await api.get('/api/coaching/interaction-types');
      setInteractionTypes(res.data);
    } catch { /* non-fatal */ }
    setEditInterOpen(true);
  };

  const handleEditInterSave = async () => {
    const values = editInterForm.getFieldsValue();
    const newList = (session.Interactions || []).map((i) => ({
      contact_id:          i.ContactID,
      note:                i.ID === editingInter.ID ? (values.note || null) : i.Note,
      type_id:             i.ID === editingInter.ID ? (values.type_id || null) : (i.TypeID || null),
      error_submission_id: i.ErrorSubmissionID || null,
      case_id:             i.CaseID || null,
    }));
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, { interactions: newList });
      message.success('Interaction updated.');
      setEditInterOpen(false);
      load();
    } catch { message.error('Failed to update interaction.'); }
  };

  // Add URL
  const handleAddUrl = async () => {
    let values;
    try { values = await urlForm.validateFields(); }
    catch { return; }

    // The update replaces every URL from this payload, and the server reads
    // lowercase url/label. Spreading the loaded rows as they are (URL/Label)
    // left every existing one with no url, so it was skipped on the way back
    // in and only the one just added survived.
    const newList = [
      ...(session.URLs || []).map((u) => ({ url: u.URL, label: u.Label })),
      { url: values.url, label: values.label || null },
    ];
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, { urls: newList });
      message.success('URL added.');
      setAddUrlOpen(false);
      load();
    } catch { message.error('Failed to add URL.'); }
  };

  // Common links picked from the list: added after the session's own, any
  // already on it skipped.
  const handleAddCommonLinks = async (links) => {
    const have = new Set((session.URLs || []).map((u) => (u.URL || '').trim()));
    const newList = [
      ...(session.URLs || []).map((u) => ({ url: u.URL, label: u.Label })),
      ...links.filter((l) => !have.has(l.URL.trim())).map((l) => ({ url: l.URL, label: l.Label || null })),
    ];
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, { urls: newList });
      message.success(links.length === 1 ? 'Link added.' : 'Links added.');
      setLinkPickerOpen(false);
      load();
    } catch { message.error('Failed to add links.'); }
  };

  const handleRemoveUrl = async (urlRow) => {
    const newList = (session.URLs || [])
      .filter((u) => u.ID !== urlRow.ID)
      .map((u) => ({ url: u.URL, label: u.Label }));
    try {
      await api.put(`/api/coaching/sessions/${sessionId}`, { urls: newList });
      load();
    } catch { message.error('Failed to remove URL.'); }
  };

  // My participant status update
  const openPartEdit = () => {
    if (!myParticipant) return;
    partForm.setFieldsValue({
      status:  myParticipant.Status,
      comment: myParticipant.Comment || '',
    });
    setPartEditOpen(true);
  };

  const handlePartSave = async () => {
    const values = partForm.getFieldsValue();
    try {
      await api.put(`/api/coaching/sessions/${sessionId}/my-participant`, {
        status:  values.status,
        comment: values.comment || null,
      });
      message.success('Updated.');
      setPartEditOpen(false);
      load();
    } catch { message.error('Failed to update.'); }
  };

  const openPartMgrEdit = (participant) => {
    setEditingPart(participant);
    partMgrForm.setFieldsValue({
      status:  participant.Status,
      comment: participant.Comment || '',
    });
    setPartMgrEditOpen(true);
  };

  const handlePartMgrSave = async () => {
    const values = partMgrForm.getFieldsValue();
    try {
      await api.put(`/api/coaching/sessions/${sessionId}/participants/${editingPart.UserID}`, {
        status:  values.status,
        comment: values.comment || null,
      });
      message.success('Participant updated.');
      setPartMgrEditOpen(false);
      load();
    } catch { message.error('Failed to update participant.'); }
  };

  const handleAddComment = async (text, files) => {
    setAddingComment(true);
    try {
      const res = await api.post(`/api/coaching/sessions/${sessionId}/comments`, { comment: text });
      const commentId = res.data.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'coaching');
        fd.append('comment_id', String(commentId));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
      loadComments();
    } catch {
      message.error('Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/api/coaching/sessions/${sessionId}/comments/${commentId}`);
      loadComments();
    } catch {
      message.error('Failed to delete comment.');
    }
  };

  const handleSaveComment = async () => {
    const text = editComment.text.trim();
    if (!text) return;
    try {
      await api.put(`/api/coaching/sessions/${sessionId}/comments/${editComment.id}`, { comment: text });
      setEditComment({ id: null, text: '' });
      loadComments();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to update comment.');
    }
  };

  const lockConfig = useLockConfig();

  // The spinner replaces the page on the first load only. Reloads after a
  // status change or edit keep the page up, so a screenshot attached to an
  // unsent comment is not thrown away.
  if (loading && !session) return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
  if (!session) return <Typography.Text type="secondary">Session not found.</Typography.Text>;

  const isAdminOrQA = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  const isCoach     = session.CoachUserID === currentUser?.ID;
  const isViewer    = (session.Viewers || []).some((v) => v.UserID === currentUser?.ID);
  const isManager   = canManage(currentUser) && (isAdminOrQA || isCoach || isViewer);
  // On the session, or Coaching Edit and up -- who the server lets comment
  // (routes/coaching _can_access_session). Coaching View on the session's
  // campaign may read it but not comment.
  const onSession   = canManage(currentUser) || isCoach || isViewer
    || session.CreatedByUserID === currentUser?.ID
    || (session.Participants || []).some((p) => p.UserID === currentUser?.ID);
  const isCanceled  = session.Status === 'canceled';

  // Agent name -> their summary, gated the way errors/<id> and the evals list
  // gate it. Two variants, because the tables are keyed differently.
  const canViewAgentSummary = (currentUser?.CanViewAgentSummary ?? 0) >= 1;

  // Evals come from the Superior call log, where the dialer agent number is
  // the identifier. Name match only as a last resort.
  const agentNameLink = (name, agentNo) => {
    if (!name) return <Typography.Text type="secondary">—</Typography.Text>;
    if (!canViewAgentSummary) return name;
    const q = agentNo
      ? `agent_no=${encodeURIComponent(agentNo)}`
      : `agent_name=${encodeURIComponent(name)}`;
    return <Typography.Link onClick={() => navigate(`/agents?${q}`)}>{name}</Typography.Link>;
  };

  // Errors and participants are keyed on the QAUser, which addresses the
  // person directly rather than matching on a name or a dialer number.
  // `tab` lands the agent summary on a specific tab. AgentDetailPanel reads it
  // off the query string, and ignores it if that tab is hidden for the viewer,
  // so passing one can never strand them on a blank tab.
  const userIdLink = (name, userId, tab) => {
    if (!name) return <Typography.Text type="secondary">—</Typography.Text>;
    if (!canViewAgentSummary || !userId) return name;
    const q = `user_id=${encodeURIComponent(userId)}`
      + (tab ? `&tab=${encodeURIComponent(tab)}` : '');
    return (
      <Typography.Link onClick={() => navigate(`/agents?${q}`)}>
        {name}
      </Typography.Link>
    );
  };
  const isCoachingLocked = session.Status === 'completed' && checkLocked(lockConfig, 'coaching', session.CreatedAt);

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space direction="vertical" size={2}>
          <Space align="center">
            <Tag color={STATUS_COLOR[session.Status]}>{STATUS_LABEL[session.Status] ?? session.Status}</Tag>
            <Typography.Title level={4} style={{ margin: 0 }}>{session.Title}</Typography.Title>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            #{session.ID} · Created by {session.CreatedByName} · {formatTS(session.CreatedAt)}
          </Typography.Text>
        </Space>
        <Space>
          {/* Copy: anyone who can create sessions, and only for a session not
              raised from an evaluation or an error -- those tie the copy to
              one agent's call. */}
          {canManage(currentUser) && !(
            (session.Interactions || []).some((i) => i.CaseID || i.ErrorSubmissionID)
            || linkedEvals.length > 0 || linkedErrors.length > 0
          ) && (
            <Button icon={<CopyOutlined />} onClick={() => setCopyOpen(true)}>Copy</Button>
          )}
          {isManager && !isCanceled && !isCoachingLocked && (
            <Button icon={<EditOutlined />} onClick={openEdit}>Edit Session</Button>
          )}
        </Space>
      </div>

      <CreateTaskFromRecord open={taskOpen} recordType="coaching" recordId={session.ID} page="coaching"
        agentUserIds={(session.Participants || []).map((p) => p.UserID)}
        onClose={() => setTaskOpen(false)}
        onCreated={() => { setTaskOpen(false); setFyiKey((k) => k + 1); }} />

      <CoachingCreateModal
        open={copyOpen}
        copyFrom={session}
        copyComment={[...comments]
          .sort((a, b) => (a.ID ?? 0) - (b.ID ?? 0))
          .find((c) => !c.IsSystem)?.Comment || ''}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setCopyOpen(false)}
      />

      {/* Overview card */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
          <Descriptions.Item label="Campaign">{session.Campaign || '—'}</Descriptions.Item>
          <Descriptions.Item label="Request Type">{session.RequestTypeName || '—'}</Descriptions.Item>
          <Descriptions.Item label="Area">{session.AreaName || '—'}</Descriptions.Item>
          <Descriptions.Item label="Coach">{session.CoachName || <Typography.Text type="secondary">Unassigned</Typography.Text>}</Descriptions.Item>
          <Descriptions.Item label="Due Date">{session.DueDate || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_COLOR[session.Status]}>{STATUS_LABEL[session.Status]}</Tag>
          </Descriptions.Item>
        </Descriptions>
        {session.Note && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Note</Typography.Text>
            <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{session.Note}</Typography.Paragraph>
          </>
        )}
      </Card>

      {/* My Status (if participant) */}
      {myParticipant && (
        <Card
          size="small"
          title={
            <Space size={6}>
              {session.Status === 'active' && <BellFilled style={{ color: '#1677ff' }} />}
              <span>My Status</span>
            </Space>
          }
          style={{
            marginBottom: 16,
            borderColor: '#1677ff',
            ...(session.Status === 'active' ? { background: '#e6f4ff' } : {}),
          }}
          extra={
            session.Status === 'active' && (
              <Button size="small" type="primary" icon={<EditOutlined />} onClick={openPartEdit}>Update</Button>
            )
          }
        >
          <Space direction="vertical" size={4}>
            <Space>
              <Tag color={PART_STATUS_COLOR[myParticipant.Status]}>
                {PART_STATUS_LABEL[myParticipant.Status]}
              </Tag>
              {myParticipant.DueDate && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Due: {myParticipant.DueDate}
                </Typography.Text>
              )}
            </Space>
            {myParticipant.Comment && (
              <Typography.Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{myParticipant.Comment}</Typography.Text>
            )}
          </Space>
        </Card>
      )}

      {/* Linked Evaluations */}
      {(linkedEvalsLoading || linkedEvals.length > 0) && (
        <Card
          title="Linked Evaluations"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table
            rowKey="ID"
            dataSource={linkedEvals}
            loading={linkedEvalsLoading}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No evaluations found.' }}
            columns={[
              {
                title: 'Contact ID', dataIndex: 'ContactID', key: 'ContactID', width: 110,
                render: (v, r) => (
                  <Link to={`/cases/${r.ID}`}>{v}</Link>
                ),
              },
              {
                title: 'Status', dataIndex: 'Status', key: 'Status', width: 150,
                render: (v) => {
                  const colors = { assigned: 'blue', in_progress: 'orange', completed: 'green', rescore_requested: 'purple', canceled: 'default' };
                  const labels = { assigned: 'Assigned', in_progress: 'In Progress', completed: 'Completed', rescore_requested: 'Rescore Requested', canceled: 'Canceled' };
                  return <Tag color={colors[v] ?? 'default'}>{labels[v] ?? v}</Tag>;
                },
              },
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 160,
                render: (v, r) => agentNameLink(v, r.AgentNo) },
              { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 110, render: (v) => v?.slice(0, 10) ?? '—' },
              {
                title: 'Form', dataIndex: 'FormName', key: 'FormName', ellipsis: true,
                render: (v, r) => v ? `${v}${r.FormVersion ? ` v${r.FormVersion}` : ''}` : '—',
              },
              {
                title: 'Score', key: 'Score', width: 90, align: 'right',
                render: (_, r) => r.TotalPossible > 0
                  ? (() => {
                      const pct = Math.round(r.TotalEarned / r.TotalPossible * 1000) / 10;
                      const color = pct >= 90 ? 'green' : pct >= 70 ? 'orange' : 'red';
                      return <Tag color={color}>{pct}%</Tag>;
                    })()
                  : <Typography.Text type="secondary">—</Typography.Text>,
              },
              {
                // "Creating a Referral?" links.
                title: 'Linked Sections', key: 'LinkedSections', width: 200,
                render: (_, r) => {
                  const secs = session?.LinkedCaseSections?.[String(r.ID)];
                  return secs?.length
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{secs.map((x) => <Tag key={x} color="blue">{x}</Tag>)}</div>
                    : <Typography.Text type="secondary">None</Typography.Text>;
                },
              },
            ]}
          />
        </Card>
      )}

      {/* Linked Errors */}
      {(linkedErrorsLoading || linkedErrors.length > 0) && (
        <Card
          title="Linked Errors"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table
            rowKey="ID"
            dataSource={linkedErrors}
            loading={linkedErrorsLoading}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No errors found.' }}
            columns={[
              {
                title: 'Error #', dataIndex: 'ID', key: 'ID', width: 90,
                render: (v) => <Link to={`/errors/${v}`}>#{v}</Link>,
              },
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 160,
                // From Linked Errors, land on the agent's Errors tab -- the
                // reason for following the link from here.
                render: (v, r) => userIdLink(v, r.AgentUserID, 'errors') },
              { title: 'Form', dataIndex: 'FormName', key: 'FormName', ellipsis: true },
              {
                title: 'All Categories', dataIndex: 'ErrorCategories', key: 'ErrorCategories', width: 200,
                render: (cats) => !cats?.length
                  ? <Typography.Text type="secondary">—</Typography.Text>
                  : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {cats.map((c) => <Tag key={c}>{c}</Tag>)}
                    </div>
                  ),
              },
              {
                // "Creating a Referral?" links.
                // An error's sections are its categories.
                title: 'Linked Categories', key: 'LinkedSections', width: 200,
                render: (_, r) => {
                  const secs = session?.LinkedErrorSections?.[String(r.ID)];
                  return secs?.length
                    ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{secs.map((x) => <Tag key={x} color="blue">{x}</Tag>)}</div>
                    : <Typography.Text type="secondary">None</Typography.Text>;
                },
              },
              {
                title: 'All Questions', dataIndex: 'ErrorQuestions', key: 'ErrorQuestions',
                render: (qs) => !qs?.length
                  ? <Typography.Text type="secondary">—</Typography.Text>
                  : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {qs.map((q, i) => <Tag key={i} color="red">{q}</Tag>)}
                    </div>
                  ),
              },
              {
                title: 'Date', dataIndex: 'DateOfError', key: 'DateOfError', width: 110,
                render: (v) => v?.slice(0, 10) ?? '—',
              },
              {
                title: 'Status', dataIndex: 'Status', key: 'Status', width: 150,
                render: (v) => {
                  const colors = { submitted: 'blue', in_progress: 'orange', completed: 'green', rescore_requested: 'purple', canceled: 'default' };
                  const labels = { submitted: 'Submitted', in_progress: 'In Progress', completed: 'Completed', rescore_requested: 'Rescore Requested', canceled: 'Canceled' };
                  return <Tag color={colors[v] ?? 'default'}>{labels[v] ?? v}</Tag>;
                },
              },
              {
                // Same history block the error details page shows, in a modal,
                // so a coach can see the agent's pattern without leaving the
                // session.
                title: '', key: 'history', width: 90,
                render: (_, r) => (
                  <Button size="small" onClick={() => setHistoryError(r)}>History</Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Modal maskClosable={false}
        open={!!historyError}
        onCancel={() => setHistoryError(null)}
        footer={null}
        width={1100}
        destroyOnClose
        title={historyError
          ? `Error History - ${historyError.AgentName || 'Agent'} (from Error #${historyError.ID})`
          : 'Error History'}
      >
        {historyError && (
          <ErrorQuestionHistory
            agentUserId={historyError.AgentUserID}
            agentName={historyError.AgentName}
            anchorDate={historyError.DateOfError || historyError.CreateDate}
            submissionId={historyError.ID}
            isCompleted={historyError.Status === 'completed'}
          />
        )}
      </Modal>

      {/* Create Task: a project task about this session (a supervisor's
          follow-up, say), linked to it -- beside the Project FYI it then
          lists in. Projects Access Edit (2), not agents. */}
      {canCreateTask && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Tooltip title="Create Project Task">
            <Button size="small" icon={<PlusOutlined />} onClick={() => setTaskOpen(true)}>Create Task</Button>
          </Tooltip>
        </div>
      )}
      {/* Project tasks about any participant that the viewer is on (hidden
          when none). Shown to anyone on the task, participants included --
          only members and assignees ever get one. */}
      <ProjectFyi key={fyiKey} page="coaching" agentUserIds={(session.Participants || []).map((p) => p.UserID)}
        campaignId={session.CampaignID} currentUser={currentUser} recordType="coaching" recordId={session.ID} />

      {/* Participants */}
      <Card size="small" title={`Participants (${session.Participants?.length ?? 0})`} style={{ marginBottom: 16 }}>
        <Table
          rowKey="ID"
          dataSource={session.Participants || []}
          size="small"
          pagination={false}
          locale={{ emptyText: 'No participants.' }}
          columns={[
            { title: 'Name',     dataIndex: 'DisplayName', key: 'DisplayName',
              render: (v, p) => userIdLink(v, p.UserID) },
            {
              title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
              render: (v) => <Tag color={PART_STATUS_COLOR[v]}>{PART_STATUS_LABEL[v] ?? v}</Tag>,
            },
            { title: 'Due Date',  dataIndex: 'DueDate',      key: 'DueDate',      width: 110, render: (v) => v || '—' },
            { title: 'Comment',   dataIndex: 'Comment',      key: 'Comment',      ellipsis: true },
            { title: 'Completed', dataIndex: 'CompletedAt',  key: 'CompletedAt',  width: 170, render: (v) => (v ? formatTS(v) : '—') },
            ...(isManager && session.Status === 'active' ? [{
              title: '', key: 'actions', width: 50,
              render: (_, p) => (
                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openPartMgrEdit(p)} />
              ),
            }] : []),
          ]}
        />
      </Card>

      {/* Viewers */}
      {(session.Viewers?.length > 0 || isManager) && (
        <Card size="small" title={`Viewers (${session.Viewers?.length ?? 0})`} style={{ marginBottom: 16 }}>
          <Space wrap>
            {(session.Viewers || []).map((v) => (
              <Tag key={v.ID} icon={<UserOutlined />}>{v.DisplayName}</Tag>
            ))}
            {session.Viewers?.length === 0 && (
              <Typography.Text type="secondary">No viewers.</Typography.Text>
            )}
          </Space>
        </Card>
      )}

      {/* Interactions */}
      <Card
        size="small"
        title={`Interactions (${(session.Interactions || []).filter((i) => i.ContactID).length})`}
        style={{ marginBottom: 16 }}
        extra={
          isManager && !isCanceled && (
            <Button size="small" icon={<PlusOutlined />} onClick={async () => {
              interForm.resetFields();
              try {
                const res = await api.get('/api/coaching/interaction-types');
                setInteractionTypes(res.data);
              } catch { /* non-fatal */ }
              setAddInterOpen(true);
            }}>
              Add
            </Button>
          )
        }
      >
        {(session.Interactions || []).filter((i) => i.ContactID).length === 0 ? (
          <Typography.Text type="secondary">No interactions linked.</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {(session.Interactions || []).filter((i) => i.ContactID).map((inter) => (
              <div key={inter.ID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Space size={8} wrap>
                    <Link to={`/calls/${inter.ContactID}`} target="_blank">
                      <Button type="link" size="small" icon={<LinkOutlined />} style={{ padding: 0 }}>
                        Contact {inter.ContactID}
                      </Button>
                    </Link>
                    {inter.InteractionID && (
                      <Typography.Link
                        href={`https://na1.nice-incontact.com/player/#/cxone-player/interactions/${inter.InteractionID}?acd-contacts=${inter.MasterContactID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12 }}
                      >
                        Play Interaction
                      </Typography.Link>
                    )}
                    {inter.SegmentID && (
                      <Typography.Link
                        href={`https://na1.nice-incontact.com/player/#/cxone-player/segments/${inter.SegmentID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12 }}
                      >
                        Play Segment
                      </Typography.Link>
                    )}
                    {inter.TypeName && <Tag style={{ marginInlineEnd: 0 }}>{inter.TypeName}</Tag>}
                    {inter.Note
                      ? <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{inter.Note}</Typography.Text>
                      : isManager && !isCanceled && <Typography.Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>no comment</Typography.Text>
                    }
                  </Space>
                  {(inter.CallDate || inter.MediaName || inter.CampaignName || inter.SkillName) && (
                    <Space size={12} style={{ paddingLeft: 2 }}>
                      {inter.CallDate && <Typography.Text type="secondary" style={{ fontSize: 11 }}>Call Date: {inter.CallDate}</Typography.Text>}
                      {inter.MediaName && <Typography.Text type="secondary" style={{ fontSize: 11 }}>Media Name: {inter.MediaName}</Typography.Text>}
                      {inter.CampaignName && <Typography.Text type="secondary" style={{ fontSize: 11 }}>Campaign: {inter.CampaignName}</Typography.Text>}
                      {inter.SkillName && <Typography.Text type="secondary" style={{ fontSize: 11 }}>Skill Name: {inter.SkillName}</Typography.Text>}
                    </Space>
                  )}
                </div>
                {isManager && !isCanceled && (
                  <Space size={4}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditInter(inter)} />
                    <Popconfirm title="Remove this interaction?" onConfirm={() => handleRemoveInteraction(inter)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )}
              </div>
            ))}
          </Space>
        )}
      </Card>

      {/* URLs */}
      <Card
        size="small"
        title={`Resources / URLs (${session.URLs?.length ?? 0})`}
        style={{ marginBottom: 16 }}
        extra={
          isManager && !isCanceled && (
            <Space>
              <Button size="small" icon={<LinkOutlined />} onClick={() => setLinkPickerOpen(true)}>
                Select Common Links
              </Button>
              <Button size="small" icon={<PlusOutlined />} onClick={() => { urlForm.resetFields(); setAddUrlOpen(true); }}>
                Add
              </Button>
            </Space>
          )
        }
      >
        {(session.URLs || []).length === 0 ? (
          <Typography.Text type="secondary">No URLs added.</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {(session.URLs || []).map((u) => (
              <div key={u.ID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <a href={u.URL} target="_blank" rel="noopener noreferrer">
                  <Button type="link" size="small" icon={<LinkOutlined />} style={{ padding: 0 }}>
                    {u.Label || u.URL}
                  </Button>
                </a>
                {isManager && !isCanceled && (
                  <Popconfirm title="Remove this URL?" onConfirm={() => handleRemoveUrl(u)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </div>
            ))}
          </Space>
        )}
      </Card>


      {/* Comments */}
      <Divider orientation="left" style={{ marginTop: 8 }}>
        <Space size={6}><CommentOutlined /><span>Comments</span></Space>
      </Divider>
      <List
        dataSource={comments}
        locale={{ emptyText: 'No comments yet.' }}
        renderItem={(c) => {
          const canEdit = !c.IsSystem && c.UserID === currentUser?.ID && dayjs().diff(dayjs(c.CreatedAt), 'minute') < 60;
          const canDelete = !c.IsSystem && currentUser?.Role === 'admin';
          const isEditing = editComment.id === c.ID;
          return (
            <List.Item style={{ padding: '8px 0' }}>
              <List.Item.Meta
                avatar={<UserAvatar userId={c.UserID} name={c.UserName} size={24} />}
                title={
                  <Space size={6}>
                    <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                    {canEdit && !isEditing && (
                      <Button type="text" size="small" icon={<EditOutlined />}
                        onClick={() => setEditComment({ id: c.ID, text: c.Comment })} />
                    )}
                    {canDelete && !isEditing && (
                      <Popconfirm title="Delete this comment?" onConfirm={() => handleDeleteComment(c.ID)} okText="Delete" okButtonProps={{ danger: true }}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </Space>
                }
                description={isEditing ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input.TextArea rows={3} value={editComment.text}
                      onChange={(e) => setEditComment(s => ({ ...s, text: e.target.value }))} autoFocus />
                    <Space>
                      <Button type="primary" size="small" onClick={handleSaveComment}>Save</Button>
                      <Button size="small" onClick={() => setEditComment({ id: null, text: '' })}>Cancel</Button>
                    </Space>
                  </Space>
                ) : (
                  <>
                    <Typography.Text strong={!!c.IsSystem} style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>
                    <CommentAttachments attachments={c.Attachments} />
                  </>
                )}
              />
            </List.Item>
          );
        }}
      />
      {onSession && (isCoach || isAdminOrQA || (isViewer && canManage(currentUser)) || session.Status === 'active') && (
        <div style={{ marginTop: 8 }}>
          <CommentBox onSubmit={handleAddComment} loading={addingComment} />
        </div>
      )}

      {/* Edit Session Modal */}
      <Modal maskClosable={false}
        title="Edit Coaching Session"
        open={editOpen}
        onOk={handleSave}
        onCancel={() => setEditOpen(false)}
        confirmLoading={saving}
        okText="Save"
        width={580}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Title" name="title" rules={[{ required: true }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item label="Campaign" name="campaign" rules={[{ required: true, message: 'Campaign is required' }]}>
            <Select
              showSearch optionFilterProp="label" placeholder="Select a campaign…"
              options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
            />
          </Form.Item>
          <Form.Item label="Request Type" name="request_type_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch optionFilterProp="label" placeholder="Select a request type…"
              options={requestTypes.map((t) => ({ label: t.Name, value: t.ID }))}
            />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true }]}
            tooltip={{
              overlayStyle: { maxWidth: 340 },
              title: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span><strong>Draft</strong> — participants do not see this in their work list</span>
                  <span><strong>Active</strong> — participants see this in their work list</span>
                  <span><strong>Completed</strong> — coaching session complete (auto set when all participants complete their individual status)</span>
                </div>
              ),
            }}
          >
            <Select options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ label: v, value: k }))} />
          </Form.Item>
          <Form.Item label="Area" name="area_id">
            <Select
              allowClear
              showSearch optionFilterProp="label" placeholder="Select an area…"
              options={areas.map((a) => ({ label: a.Name, value: a.ID }))}
            />
          </Form.Item>
          <Form.Item label="Coach" name="coach_user_id">
            <Select
              showSearch optionFilterProp="label" allowClear placeholder="Assign a coach…"
              options={coaches.map((c) => ({ label: c.DisplayName, value: c.ID }))}
            />
          </Form.Item>
          <Form.Item label="Due Date" name="due_date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Participants" name="participant_ids" rules={[{ required: true }]}>
            <Select
              mode="multiple" showSearch optionFilterProp="label" placeholder="Select participants…"
              options={participantCandidates.map((u) => ({
                label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
                value: u.ID,
              }))}
            />
          </Form.Item>
          <ParticipantBulkAdd
            users={participantCandidates}
            value={watchedParticipants || []}
            onChange={(ids) => editForm.setFieldValue('participant_ids', ids)}
            noun="participants"
          />
          <Form.Item label="Viewers" name="viewer_ids">
            <Select
              mode="multiple" showSearch optionFilterProp="label" allowClear placeholder="Select viewers…"
              options={viewerCandidates.map((u) => ({
                label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
                value: u.ID,
              }))}
            />
          </Form.Item>
          <ParticipantBulkAdd
            users={viewerCandidates}
            value={watchedViewers || []}
            onChange={(ids) => editForm.setFieldValue('viewer_ids', ids)}
            noun="viewers"
          />
          <Form.Item label="Note" name="note">
            <Input.TextArea rows={3} maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal maskClosable={false}
        title="Due date is today or in the past"
        open={!!dueCheck}
        onCancel={() => setDueCheck(null)}
        width={460}
        footer={[
          <Button key="back" onClick={() => setDueCheck(null)}>Go Back</Button>,
          <Button key="keep" onClick={() => { const v = dueCheck.values; setDueCheck(null); doSave(v); }}>
            Keep Date &amp; Activate
          </Button>,
          <Button key="update" type="primary" disabled={!newDue}
            onClick={() => {
              const v = { ...dueCheck.values, due_date: newDue };
              editForm.setFieldValue('due_date', newDue);
              setDueCheck(null);
              doSave(v);
            }}>
            Update Due Date &amp; Activate
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          This session is due {dueCheck?.values?.due_date?.format('YYYY-MM-DD')}. Pick a new due date before
          making it Active, or keep the current one.
        </div>
        <DatePicker
          style={{ width: '100%' }}
          value={newDue}
          onChange={setNewDue}
          disabledDate={(d) => !!d && !d.isAfter(dayjs(), 'day')}
          placeholder="New due date"
        />
      </Modal>

      {/* Add Interaction Modal */}
      <Modal maskClosable={false}
        title="Add Interaction"
        open={addInterOpen}
        onOk={handleAddInteraction}
        onCancel={() => setAddInterOpen(false)}
        okText="Add"
        destroyOnClose
      >
        <Form form={interForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Contact ID" name="contact_id" rules={[{ required: true, message: 'Contact ID is required' }]}>
            <Input placeholder="e.g. 12345678" />
          </Form.Item>
          <Form.Item label="Type" name="type_id">
            <Select
              allowClear
              placeholder="Select a type (optional)"
              options={interactionTypes.map((t) => ({ label: t.Name, value: t.ID }))}
            />
          </Form.Item>
          <Form.Item label="Comment" name="note">
            <Input.TextArea rows={2} placeholder="Optional comment about this interaction…" maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Interaction Modal */}
      <Modal maskClosable={false}
        title={`Edit Interaction — Contact ${editingInter?.ContactID}`}
        open={editInterOpen}
        onOk={handleEditInterSave}
        onCancel={() => setEditInterOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={editInterForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Type" name="type_id">
            <Select
              allowClear
              placeholder="Select a type (optional)"
              options={interactionTypes.map((t) => ({ label: t.Name, value: t.ID }))}
            />
          </Form.Item>
          <Form.Item label="Comment" name="note">
            <Input.TextArea rows={3} placeholder="Optional comment about this interaction…" maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <CommonLinksPicker
        open={linkPickerOpen}
        campaignId={session.CampaignID}
        existingUrls={(session.URLs || []).map((u) => u.URL)}
        onClose={() => setLinkPickerOpen(false)}
        onPick={handleAddCommonLinks}
      />

      {/* Add URL Modal */}
      <Modal maskClosable={false}
        title="Add Resource URL"
        open={addUrlOpen}
        onOk={handleAddUrl}
        onCancel={() => setAddUrlOpen(false)}
        okText="Add"
        destroyOnClose
      >
        <Form form={urlForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="URL" name="url" rules={[{ required: true, message: 'URL is required' }, { type: 'url', message: 'Enter a valid URL' }]}>
            <Input placeholder="https://…" />
          </Form.Item>
          <Form.Item label="Label" name="label">
            <Input placeholder="Friendly label (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* My Participant Update Modal */}
      <Modal maskClosable={false}
        title="Update My Status"
        open={partEditOpen}
        onOk={handlePartSave}
        onCancel={() => setPartEditOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={partForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="My Status" name="status">
            <Select options={[
              { label: 'Pending',   value: 'pending' },
              { label: 'Completed', value: 'completed' },
            ]} />
          </Form.Item>
          <Form.Item label="Comment" name="comment">
            <Input.TextArea rows={3} placeholder="Optional comment…" maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* Manager: Edit Participant Modal */}
      <Modal maskClosable={false}
        title={editingPart ? `Update — ${editingPart.DisplayName}` : 'Update Participant'}
        open={partMgrEditOpen}
        onOk={handlePartMgrSave}
        onCancel={() => setPartMgrEditOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={partMgrForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Status" name="status">
            <Select options={[
              { label: 'Pending',   value: 'pending' },
              { label: 'Completed', value: 'completed' },
            ]} />
          </Form.Item>
          <Form.Item label="Comment" name="comment">
            <Input.TextArea rows={3} placeholder="Optional comment…" maxLength={4000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
